import { lang } from '@umu/ukit-lang';
import React, { useState, useContext, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import { components } from 'common';
import './list.scss';
import {
    IListItem,
    EPlatformTravelType as TravelType,
    EPlatformSectionType as SectionType,
    IDepartmentList,
    IResponse,
    DEPARTMENT_ACCESS,
    EnterpriseRoleType,
} from '@umu/umu-types';
import { formatDepartmentName } from '../../utils';
import {
    getDepartmentListByLevel,
    IDepartListInfo,
    getDepartmentListByManagerId,
    getDepartmentList,
    IDepartListInfo as ISubDepartListInfo,
    getSubDepartmentList,
    ISubDepartListInfo as ISubDepartInfo,
    getSugDepartment,
    ISugDepartListInfo,
    getSugMember,
    ISugMemberInfo,
    IMemberListInfo,
} from '@umu/umu-api';
import { DialogContext, DIALOG_ACTION_TYPE, IDepartmentCache } from '../../dialogContext';
import { SnackBar } from '@umu/umu-design';

import { DepartmentSearch } from '../../../@departmentSearch/departmentSearch';

import { Checkbox, ListItem, CHECKBOX_CHECK_TYPE } from './listItem';
import leftIcon from '../../images/icon-left.png';
import { getMemberDataInfo, onTransformMemberData, getMemberInfoByUmuIds as getMemberInfoByUmuIdsService, normMemberItem } from './dataFlow';

export const compare = (p: string) => {
    return (a: any, b: any) => {
        return Number(a[p]) - Number(b[p]);
    };
};

interface IListProps {
    type: TravelType;
    isFromDrop?: boolean; // 是否下拉选择框
    section: SectionType;
    hasAllPermissionRoles?: EnterpriseRoleType[];
    departmentId?: string;
    expendParams?: {
        [propName: string]: string;
    };// 接口额外扩展参数
    excludeDepartmentIds?: string; // 排除的部门id，包括其子部门
    isRadio?: boolean;
    source?: string;
    defaultSelectedList?: string;
    clsName?: string;
    isAllowSelect?: boolean; // 是否展示全选
    isShowMember?: boolean; // 是否展示成员数量
    checkableRoot?: boolean; // 是否开放选择根部门
    allSelectText?: string; // 全选展示文案
    limit?: number; // 多选限制
    access?: DEPARTMENT_ACCESS;
    dataRemoveCb?(key: number, item: IListItem): void;
    dataAddCb?(key: number, item: IListItem): void;
    clean?(): void;
}

export interface IDListRef {
    cleanStateByParent: () => void;
}
const { If, PreImg, List, ListLoading, Loading } = components;

function DList(props: IListProps, ref: React.Ref<IDListRef>) {
    const context = useContext(DialogContext);
    const hasAllPermissionRoles = useMemo(() => (props.hasAllPermissionRoles || [EnterpriseRoleType.MANAGE, EnterpriseRoleType.ADMIN]), [props.hasAllPermissionRoles]);
    const [popoverLeft, setPopoverLeft] = useState(0);
    const [popoverTop, setPopoverTop] = useState(0);
    const [isShowPopover, setShowPopover] = useState(false);
    const [popoverName, setPopoverName] = useState('');
    const [isAllSelect, setIsAllSelect] = useState<CHECKBOX_CHECK_TYPE>(CHECKBOX_CHECK_TYPE.UN_CHECK);

    const {
        dataList = [],
        isSearchMode = false,
        departmentCache,
        enterpriseName,
        selectedMap,
        pageInfo,
        uplevelDepartment,
        uplevelIdMap,
    } = context.state;
    const dispatch = context.dispatch;
    const defaultSelectedList: string[] = props.defaultSelectedList ? props.defaultSelectedList.split(',') : [];

    useEffect(() => {
        if (props.section === SectionType.left) {
            setDefaultList();
            switch (props.type) {
                case TravelType.department:
                    loadUplevelDepartment().then((uplevelDepartmentList: IListItem[]) => {
                        // loadDepartmentData(props.departmentId);
                        const showDepartmentList: IListItem[] = [];
                        if (props.departmentId && props.departmentId !== '0' && props.departmentId !== '-1') {
                            loadDepartmentData(props.departmentId);
                            loadDepartmentInfoData(props.departmentId);
                        } else {
                            const departmentMapId: {
                                [key: string]: string;
                            } = {};
                            uplevelDepartmentList.forEach((i: IListItem) => {
                                if (i.oriData.parent_department_id === '0') {
                                    if (departmentMapId[i.id]) {
                                        return;
                                    }
                                    departmentMapId[i.id] = i.id;
                                    showDepartmentList.push(i);
                                }
                            });
                            const sortList: IListItem[] = showDepartmentList.sort((a: IListItem, b: IListItem) => {
                                return Number(a.oriData.show_index) - Number(b.oriData.show_index);
                            });
                            setListState(sortList, '0', false);
                        }
                    });
                    break;
                case TravelType.owner:
                    loadMemberData(props.departmentId, false, true);
                    break;
            }
        }
        return () => { };
    }, []);
    useEffect(() => {
        if (props.section === SectionType.left) {
            checkoutIsAllSelect(dataList);
        }
    }, [dataList])
    useImperativeHandle(ref, () => ({
        cleanStateByParent() {
            setCleanState();
        },
    }));
    /**
     * @description 全选按钮展示
     */
    const onSellectChange = (isChecked: boolean) => {
        dispatch({
            type: DIALOG_ACTION_TYPE.SET_ALL_LIST_CHECKED,
            payload: {
                isChecked,
            },
        });
    }

    const getMemberInfoByUmuIds = async (umu_ids: string) => {
        if (!umu_ids) {
            return [];
        }
        try {
            const result = await getMemberInfoByUmuIdsService({ umu_ids });
            return result.list.map((item: IMemberListInfo) => normMemberItem(item)) as IListItem[];
        } catch (error) {
            console.log('getMemberInfoByUmuIds 发生错误', error);
            return [];
        }
    };

    const renderAllSelect = () => {
        return (
            <div className='is-all-select'>
                <div className='check-box'>
                    <Checkbox
                        isChecked={isAllSelect}
                        onChange={onSellectChange}
                    />
                </div>
                <div className='select-all-text'>{props.allSelectText || lang('全选')}</div>
            </div>
        );
    }
    const setDefaultList = async () => {
        let defaultExcludeList: string[] = [];
        if (props.excludeDepartmentIds) {
            defaultExcludeList = props.excludeDepartmentIds.split(',');
        }

        dispatch({
            type: DIALOG_ACTION_TYPE.SET_DEFAULT_SELECTED_LIST,
            payload: { defaultSelectedList, defaultExcludeList },
        });

        if (props.type === TravelType.department) {
            defaultSelectedList.forEach((item) => {
                loadDefaultSelectedInfoData(item);
            });
        }

        if(props.type === TravelType.owner){
            const result = await getMemberInfoByUmuIds(defaultSelectedList.join(","));
            dispatch({
                type: DIALOG_ACTION_TYPE.UPDATE_SELECTED_MAP,
                payload: {
                    list: result,
                },
            });
        }
    };

    const setCleanState = () => {
        dispatch({
            type: DIALOG_ACTION_TYPE.CLEAN,
            payload: {},
        });
    };
    const setListItemState = (isAdd: boolean, item: IListItem) => {
        dispatch({
            type: DIALOG_ACTION_TYPE.SET_DATAITEM,
            payload: {
                item,
                isAdd,
                isRadio: props.isRadio,
            },
        });
    };

    const setListState = (list: IListItem[], departmentId: string, isMore: boolean = false, pInfo?: any) => {
        dispatch({
            type: DIALOG_ACTION_TYPE.SET_DATALIST,
            payload: {
                list,
                currDepartmentId: departmentId,
                isMore,
                pInfo: !pInfo ? null : pInfo,
            },
        });
    };

    const setDepartmentCacheState = (eName: string | null, department: IDepartmentCache) => {
        dispatch({
            type: DIALOG_ACTION_TYPE.SET_CACHE,
            payload: {
                departmentCache: department,
                enterpriseName: eName,
            },
        });
    };

    const setSugState = (list: IListItem[]) => {
        dispatch({
            type: DIALOG_ACTION_TYPE.SET_DATASUG,
            payload: {
                list,
                isSearchMode: true,
            },
        });
    };

    const sugDepartmentData = (keywords: string) => {
        let paramsSug = {
            keywords,
        };
        if (props.source) {
            paramsSug = Object.assign(paramsSug, { source: props.source });
        }
        if (props.expendParams) {
            paramsSug = Object.assign(paramsSug, { ...props.expendParams });
        }
        getSugDepartment(paramsSug).then((list: ISugDepartListInfo[]) => {
            if (!list) {
                return;
            }
            const data: IListItem[] = [];
            list.forEach((item: ISugDepartListInfo) =>
                data.push(
                    Object.assign(
                        { isSelected: false, type: TravelType.department, checkable: true },
                        {
                            name: item.department_name,
                            id: item.department_id,
                            oriData: item,
                        },
                    ),
                ),
            );
            setSugState(data);
        });
    };

    const sugMemberData = (keywords: string) => {
        const params = {
            keywords,
            department_id: props.departmentId || '0',
        };
        getSugMember(params).then((list: ISugMemberInfo[]) => {
            if (!list) {
                return;
            }
            const data: IListItem[] = [];
            list.forEach((item: ISugMemberInfo) =>
                data.push(
                    Object.assign(
                        { isSelected: false, type: TravelType.owner, checkable: true },
                        {
                            name: item.user_name,
                            id: item.umu_id,
                            avatar: item.avatar || '',
                            oriData: {
                                user_info: item,
                                user_name: item.user_name,
                                umu_id: item.umu_id,
                            },
                        },
                    ),
                ),
            );
            setSugState(data);
        });
    };
    const loadDefaultSelectedInfoData = (departmentId: string) => {
        if (!departmentId || departmentId === '0' || departmentId === '-1') {
            // 顶级部门
            dispatch({
                type: DIALOG_ACTION_TYPE.SET_DEFAULT_SELECTED_DATAINFO,
                payload: {
                    info: genTopDepartmentData(enterpriseName),
                },
            });
            return;
        }
        const params = {
            department_id: departmentId,
        };
        getSubDepartmentList(params).then((info: ISubDepartInfo) => {
            const listItem: IListItem = Object.assign(
                { isSelected: false, type: TravelType.department, checkable: true },
                {
                    name: info.department_name,
                    id: info.department_id,
                    oriData: info,
                    isHaveNextLevel: !!(info.child_path && info.child_path.length > 0),
                },
            );
            dispatch({
                type: DIALOG_ACTION_TYPE.SET_DEFAULT_SELECTED_DATAINFO,
                payload: {
                    info: listItem,
                },
            });
        });
    };

    /**
     * 获取当前用户可以管理的部门列表，及父部门列表并缓存起来，
     */
    const loadUplevelDepartment = () => {
        const uplevelDepartmentList: IListItem[] = [];
        let paramsLevel = {};
        if (props.access) {
            paramsLevel = Object.assign(paramsLevel, { type: props.access });
        }
        if (props.source) {
            paramsLevel = Object.assign(paramsLevel, { source: props.source });
        }
        if (props.expendParams) {
            paramsLevel = Object.assign(paramsLevel, { ...props.expendParams });
        }
        return getDepartmentListByManagerId(paramsLevel).then((info: IDepartListInfo) => {
            const list: IDepartmentList[] = info.department_list;
            const parentIdMap: {
                [key: string]: string;
            } = {};
            parentIdMap['-1'] = '0';
            let upId = '0';
            uplevelDepartmentList.push({
                isSelected: defaultSelectedList.indexOf('0') !== -1,
                checkable: true,
                type: TravelType.department,
                name: info.show_name || '',
                id: '0',
                oriData: {
                    department_id: '0',
                    department_name: info.show_name || '',
                    manage_permission: info.manage_permission
                },
                isHaveNextLevel: list.length > 0,
            });

            // 分两步进行的原因：有限显示有权限的部门，
            list.forEach((item: IDepartmentList) => {
                uplevelDepartmentList.push({
                    isSelected: false,
                    checkable: true,
                    type: TravelType.department,
                    name: item.department_name,
                    id: item.department_id,
                    isHaveNextLevel: item.child_path.length > 0,
                    oriData: item,
                });
                parentIdMap[item.parent_department_id] = parentIdMap[item.parent_department_id]
                    ? parentIdMap[item.parent_department_id] + ',' + item.department_id
                    : item.department_id;
            });
            list.forEach((item: IDepartmentList) => {
                let currId = item.department_id;
                if (item.parent_path && item.parent_path.length > 0) {
                    item.parent_path.forEach((p, index) => {
                        currId = p.department_id;
                        parentIdMap[upId] = parentIdMap[upId] ? parentIdMap[upId] + ',' + currId : currId;
                        uplevelDepartmentList.push({
                            isSelected: false,
                            type: TravelType.department,
                            name: p.department_name,
                            id: p.department_id,
                            isHaveNextLevel: true,
                            oriData: {
                                department_name: p.department_name,
                                department_id: p.department_id,
                                parent_path: item.parent_path.slice(0, index),
                                parent_department_id: upId,
                            },
                        });
                        upId = currId;
                    });
                    upId = '0';
                }
            });
            dispatch({
                type: DIALOG_ACTION_TYPE.SET_UPLEVEL_DEPARTMENT,
                payload: {
                    uplevelDepartmentList,
                    uplevelIdMap: parentIdMap,
                },
            });
            const dCache: IDepartmentCache = {
                currDepartmentId: '0',
                currDepartmentName: '',
                upLevelDepartmentId: '-1',
                parents: [],
                children: [],
            };
            setDepartmentCacheState((info && info.show_name) || null, dCache);
            return Promise.resolve(uplevelDepartmentList);
        });
    };
    const loadDepartmentInfoData = (departmentId: string) => {
        if (!departmentId || departmentId === '0' || departmentId === '-1') {
            // 顶级部门
            return;
        }
        const params = {
            department_id: departmentId,
        };
        getSubDepartmentList(params).then((info: ISubDepartInfo) => {
            const dCache: IDepartmentCache = {
                currDepartmentId: info.department_id,
                currDepartmentName: info.department_name,
                upLevelDepartmentId: info.parent_department_id,
                parents: info.parent_path,
                children: info.child_path,
            };
            setDepartmentCacheState((info && info.enterprise_show_name) || null, dCache);
        });
    };
    const loadDepartmentData = (departmentId?: string, isMore: boolean = false) => {
        let paramsDepartment: any = {};
        if (props.access) {
            paramsDepartment = Object.assign(paramsDepartment, { type: props.access });
        }
        if (props.source) {
            paramsDepartment = Object.assign(paramsDepartment, { source: props.source});
        }
        if (props.expendParams) {
            paramsDepartment = Object.assign(paramsDepartment, { ...props.expendParams });
        }
        const retData: IListItem[] = [];
        const tmpMap: { [key: string]: string } = {}; // 缓存起来，去重
        if (!departmentId || departmentId === '0' || departmentId === '-1') {
            // 顶级部门
            paramsDepartment.level = 1;

            getDepartmentListByLevel(paramsDepartment).then((res: IResponse<IDepartListInfo>) => {
                const info = res.data;
                const list: IDepartmentList[] = info.department_list;
                list.sort(compare('show_index'));
                list.forEach((item: IDepartmentList) => {
                    tmpMap[item.department_id] = '1';
                    retData.push(
                        Object.assign(
                            { isSelected: false, type: TravelType.department, checkable: true },
                            {
                                name: item.department_name,
                                id: item.department_id,
                                oriData: item,
                                isHaveNextLevel:
                                    !!(item.child_path && item.child_path.length > 0) ||
                                    !!item.have_child_departments_flag,
                            },
                        ),
                    );
                });
                // 将没有权限，但是子部门有权限的部门假如列表
                const tmpData: IListItem[] = loadDepartmentFromUplevel(departmentId) || [];
                tmpData.forEach((item: IListItem) => {
                    if (!tmpMap[item.id]) {
                        retData.push(item);
                    }
                });
                const dCache: IDepartmentCache = {
                    currDepartmentId: departmentId || '0',
                    currDepartmentName: '',
                    upLevelDepartmentId: '-1',
                    parents: [],
                    children: list,
                };
                if (departmentId === '-1') {
                    // 部门ID=-1 默认只显示企业，不显示一级菜单。
                    const hasNextLevel = retData.length > 0;
                    retData.splice(0, retData.length);
                    retData.push(genTopDepartmentData(info.show_name, hasNextLevel, info.manage_permission));
                }
                setDepartmentCacheState(info.show_name, dCache);
                setListState(retData, paramsDepartment.department_id, isMore);
            });
        } else {
            paramsDepartment.department_id = departmentId;
            getDepartmentList(paramsDepartment).then((subData: ISubDepartListInfo) => {
                if (!subData) {
                    return;
                }
                const list: IDepartmentList[] = subData.department_list;
                list.sort(compare('show_index'));
                list.forEach((item: IDepartmentList) => {
                    tmpMap[item.department_id] = '1';
                    retData.push(
                        Object.assign(
                            { isSelected: false, type: TravelType.department, checkable: true },
                            {
                                name: item.department_name,
                                id: item.department_id,
                                oriData: item,
                                isHaveNextLevel:
                                    !!(item.child_path && item.child_path.length > 0) ||
                                    !!item.have_child_departments_flag,
                            },
                        ),
                    );
                });
                // 将没有权限，但是子部门有权限的部门假如列表
                const tmpData: IListItem[] = loadDepartmentFromUplevel(departmentId) || [];
                tmpData.forEach((item: IListItem) => {
                    if (!tmpMap[item.id]) {
                        retData.push(item);
                    }
                });
                setListState(retData, paramsDepartment.department_id, isMore);
            });
        }
    };

    const loadDepartmentFromUplevel = (departmentId?: string): IListItem[] => {
        const list: IListItem[] = [];
        const id = departmentId ? departmentId : '0';
        let ids = (uplevelIdMap[id] && uplevelIdMap[id].split(',')) || [];
        ids = Array.from(new Set(ids));
        ids.forEach((i) => {
            if (uplevelDepartment[i]) {
                list.push(uplevelDepartment[i]);
            }
        });
        return list;
    };
    const genTopDepartmentData = (eName: string, isHaveNextLevel = true, managePermission?: number) => {
        // 把企业构造成顶级部门，方便显示
        const item: IListItem = Object.assign(
            { isSelected: false, type: TravelType.department },
            {
                name: eName || '',
                id: '0',
                checkable: true,
                oriData: {
                    department_id: '0',
                    department_name: eName || '',
                    manage_permission: managePermission
                },
                isHaveNextLevel,
            },
        );
        return item;
    };

    const loadMemberData = async (departmentId?: string, isMore: boolean = false, isInit: boolean = false) => {
        const params = {
            department_id: departmentId || '0',
            page: isMore ? pageInfo.currentPage + 1 : 1,
            size: pageInfo.size,
        };
        const dCache: IDepartmentCache = {
            currDepartmentId: departmentId || '0',
            currDepartmentName: '',
            upLevelDepartmentId: '-1',
            parents: [],
            children: [],
        };
        setDepartmentCacheState(null, dCache);
        const listInfo = await getMemberDataInfo(params, {
            defaultSelectedList,
            isInit,
            onTransform: onTransformMemberData
        })
        setListState(listInfo.list, departmentId || '0', isMore, listInfo.pageInfo);
    };

    const onClickRemove = (value: IListItem) => {
        setListItemState(false, value);
    };

    const checkoutIsAllSelect = (list: IListItem[]) => {
        if (!props.isAllowSelect) {
            return;
        }
        const filterData = list.filter((item) => {
            return item.isSelected;
        })
        let checkStatus = !!filterData.length ? CHECKBOX_CHECK_TYPE.UNKNOWN_CHECK : CHECKBOX_CHECK_TYPE.UN_CHECK;
        if (filterData.length === list.length && !!list.length) {
            checkStatus = CHECKBOX_CHECK_TYPE.IS_CHECK
        }
        setIsAllSelect(checkStatus);
    }
    const onNextLevel = (value: IListItem) => {
        loadDepartmentInfoData(value.id);
        loadDepartmentData(value.id);
    };

    const onUpLevel = () => {
        const departmentId = departmentCache && departmentCache.upLevelDepartmentId;
        if (props.excludeDepartmentIds && props.excludeDepartmentIds === '0' && departmentId === '-1') {
            return;
        }

        loadDepartmentInfoData(departmentId);
        loadDepartmentData(departmentId);
    };

    const onSelectedChange = (params: IListItem, isChecked: any) => {
        setListItemState(isChecked, params);
    };
    const charFilterSpaces = (value: string) => {
        const result = value.replace(/\s+/g, '');
        return result.length > 0;
    };
    const searchCallback = (keywords: string | undefined | null) => {
        if (keywords && charFilterSpaces(keywords)) {
            if (props.type === TravelType.department) {
                sugDepartmentData(keywords);
            } else if (props.type === TravelType.owner) {
                sugMemberData(keywords);
            }
        } else {
            const departmentId = (departmentCache && departmentCache.currDepartmentId) || '0';
            if (props.type === TravelType.department) {
                loadDepartmentData(departmentId);
            } else if (props.type === TravelType.owner) {
                loadMemberData(departmentId, false, false);
            }
        }
    };

    let showDataList = dataList;
    const selectedValues = Object.values(selectedMap);
    const selectedNum = selectedValues.length;
    if (props.section === SectionType.right) {
        showDataList = selectedValues;
    }

    const limitValidate = (disabledCheck: boolean) => {
        return () => {
            if (disabledCheck) {
                SnackBar.show(lang('最多选择{$0{0=个部门, 1=个部门}}'));
            }
        };
    };

    const renderListHeader = () => {
        const travelType = props.type;
        const section = props.section;
        const name = formatDepartmentName(
            true,
            enterpriseName,
            departmentCache.currDepartmentName,
            '-1',
            departmentCache.parents,
        );
        const currDepartmentId = departmentCache.currDepartmentId;
        const excludeDepartmentIds = props.excludeDepartmentIds;
        if (travelType === TravelType.department && section === 'left' && !isSearchMode) {
            if (currDepartmentId === '0' && excludeDepartmentIds === '0') {
                return (
                    <div className="list-header-content">
                        <span className="list-header-name list-header-name-right">{name}</span>
                    </div>
                );
            }
            if (currDepartmentId === '-1' || !currDepartmentId) {
                return (
                    <div className="list-header-content">
                        <span className="list-header-name"></span>
                    </div>
                );
            }
            const params = uplevelDepartment['0'];
            const isRootDepartment = currDepartmentId === '0';
            if (isRootDepartment && params && props.checkableRoot) {
                const isSelected = selectedValues.findIndex((v) => v.id === '0') !== -1;
                const disabledCheck = (!isSelected && !!props.limit && selectedNum >= props.limit);
                return (
                    <div className="list-header-content root-department" onClick={limitValidate(disabledCheck)}>
                        <Checkbox
                            isChecked={isSelected ? CHECKBOX_CHECK_TYPE.IS_CHECK : CHECKBOX_CHECK_TYPE.UN_CHECK}
                            onChange={(isChecked) => onSelectedChange({ ...params, isSelected }, isChecked)}
                            disabledCheck={disabledCheck}
                        />
                        <span className="list-header-name">{name}</span>
                    </div>
                );
            }

            return (
                <div className="list-header-content">
                    <PreImg>
                        <img className="list-header-icon" src={leftIcon} onClick={() => onUpLevel()} alt="" />
                    </PreImg>
                    <span className="list-header-name">{name}</span>
                </div>
            );
        } else {
            return '';
        }
    };

    /**
     * 下拉列表的头部
     * 在用户管理搜索列表中用到
     */
    const renderDropListHeader = () => {
        const travelType = props.type;
        const section = props.section;
        const name = lang('返回上一级');
        if (travelType === TravelType.department && section === 'left' && !isSearchMode) {
            return (
                <div className="list-header-content">
                    {departmentCache.currDepartmentId === '-1' ||
                        departmentCache.currDepartmentId === '0' ||
                        !departmentCache.currDepartmentId ? (
                        <span className="list-header-name">{''}</span>
                    ) : (
                        <>
                            <PreImg>
                                <img className="list-header-icon" src={leftIcon} onClick={() => onUpLevel()} alt="" />
                            </PreImg>
                            <span className="list-header-name list-header-name-drop" onClick={() => onUpLevel()}>
                                {name}
                            </span>
                        </>
                    )}
                </div>
            );
        } else {
            return '';
        }
    };

    function renderItem(item: any, index: number) {
        if (!item.id) {
            return <></>;
        }
        return (
            <ListItem
                itemData={item}
                index={index}
                limit={props.limit}
                selectedNum={selectedNum}
                travelType={props.type}
                isShowMember={props.isShowMember}
                sectionType={props.section}
                onClickRemove={onClickRemove}
                onNextLevel={onNextLevel}
                onSelectedChange={onSelectedChange}
                onShowPopName={onShowPopName}
                onToggleName={onToggleName}
                enterpriseName={enterpriseName}
                isSearchMode={isSearchMode}
                checkableRoot={props.checkableRoot}
                hasAllPermissionRoles={hasAllPermissionRoles}
            />
        );
    }

    const renderSearch = () => {
        const emptyMsg = props.type === TravelType.department ? lang('未搜索到相关部门') : lang('未搜索到相关成员');
        if (props.section === SectionType.left) {
            let emptyDiv = <></>;
            if (dataList.length < 1 && isSearchMode) {
                emptyDiv = (
                    <div className="list-header-empty">
                        <span className="name">{emptyMsg}</span>
                    </div>
                );
            }
            return (
                <div className="search">
                    <DepartmentSearch searchCallback={searchCallback} />
                    {emptyDiv}
                </div>
            );
        } else {
            return null;
        }
    };

    const loadMore = () => {
        if (props.type === TravelType.department) {
            loadDepartmentData(departmentCache.currDepartmentId, true);
        } else if (props.type === TravelType.owner) {
            loadMemberData(departmentCache.currDepartmentId, true, false);
        }
    };

    const renderPopoverContent = () => {
        return (
            <If condition={isShowPopover}>
                <div className="dp-popover" style={{ left: popoverLeft, top: popoverTop }}>
                    <div className="dp-popover-content">
                        <span className="dp-popover-name">{popoverName}</span>
                    </div>
                </div>
            </If>
        );
    };

    const onShowPopName = (isShow: boolean, left: number, top: number, fullName: string) => {
        if (!isShow) {
            setShowPopover(false);
        } else if (fullName) {
            setPopoverLeft(left);
            setPopoverTop(top);
            setPopoverName(fullName);
            setShowPopover(true);
        }
    };

    const onToggleName = (params: IListItem) => {
        setListItemState(!params.isSelected, params);
    };

    const totalPage = props.section === SectionType.left ? pageInfo.totalPage : 1;
    const currentPage = props.section === SectionType.left ? pageInfo.currentPage : 1;
    const clsNameContent = props.clsName ? 'list-content-' + props.clsName + ' ' : ' ';
    const clsNameSearch = props.clsName ? 'list-search-' + props.clsName + ' ' : ' ';

    return (
        <div className="list-panel biz-depart-list">
            <div className='biz-depart-toolbar'>
                {props.isAllowSelect ? renderAllSelect() : null}
                {renderPopoverContent()}
                <div className={clsNameSearch + ' list-search'}>{renderSearch()}</div>
            </div>
            <div className={`${clsNameContent} list-content ${props.isAllowSelect ? 'list-content-min' : ''}`}>
                {props.isFromDrop ? renderDropListHeader() : renderListHeader()}
                {/* { renderList(props) } */}
                <List
                    totalPageNum={totalPage}
                    currentPage={currentPage}
                    load={loadMore}
                    isLoading={false}
                    dataList={showDataList}
                    renderItem={renderItem}
                >
                    <ListLoading>
                        <Loading />
                    </ListLoading>
                </List>
            </div>
        </div>
    );
}

const DListView = forwardRef(DList);
export default DListView;
